import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import { Brain, Shield, Menu, LogIn } from "lucide-react";
import { JourneySection } from "./sections/JourneySection";
import { useHeroScrollParallax } from "./hooks/useHeroScrollParallax";
import { HeroSection } from "./sections/HeroSection";
import { ProblemSection } from "./sections/ProblemSection";
import { ModulesSection } from "./sections/ModulesSection";
import { AiSection } from "./sections/AiSection";
import { PersonasSection } from "./sections/PersonasSection";
import { TrustSection } from "./sections/TrustSection";
import { TestimonialsSection } from "./sections/TestimonialsSection";
import { PlansSection } from "./sections/PlansSection";
import { FaqSection } from "./sections/FaqSection";
import { FinalCta } from "./sections/FinalCta";

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

const NAV_LINKS = [
  { id: "modulos", label: "Módulos" },
  { id: "ia", label: "IA" },
  { id: "ecossistema", label: "Ecossistema" },
  { id: "planos", label: "Planos" },
  { id: "depoimentos", label: "Resultados" },
] as const;

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const { sectionRef: heroSectionRef, scrollProgress: heroScrollProgress } =
    useHeroScrollParallax();

  const handleLogin = () => navigate("/login");
  const goInstitutionWithPlan = (planId: string) => {
    navigate(`/cadastro-instituicao?plano=${planId}`);
    setMobileNavOpen(false);
  };

  const scrollToSection = (id: string) => {
    scrollToId(id);
    setMobileNavOpen(false);
  };

  return (
    <div className="min-h-screen w-full max-w-[100vw] overflow-x-hidden bg-[#050a14] text-white supports-[overflow:clip]:overflow-x-clip">
      {/* Fixed ambient gradient */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(45,212,191,0.12) 0%, transparent 45%), radial-gradient(circle at 80% 10%, rgba(6,182,212,0.1) 0%, transparent 40%)",
        }}
      />

      {/* ── Navigation ─────────────────────────────────────────────── */}
      <header className={`fixed inset-x-0 top-0 z-50 pt-[env(safe-area-inset-top,0px)] transition-all duration-300 ${
        scrolled
          ? "border-b border-white/[0.08] bg-[#050a14]/95 shadow-[0_1px_24px_rgba(0,0,0,0.4)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent backdrop-blur-none"
      }`}>
        <div className="container mx-auto flex max-w-7xl items-center justify-between gap-2 py-3 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:px-6 sm:py-3.5">
          {/* Brand */}
          <button
            type="button"
            onClick={() => scrollToId("top")}
            className="flex min-w-0 items-center gap-2 text-left opacity-90 transition hover:opacity-100 sm:gap-3"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 shadow-lg shadow-teal-500/25 sm:h-10 sm:w-10">
              <Brain className="h-4 w-4 text-white sm:h-5 sm:w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <span className="font-display block truncate text-base font-semibold tracking-tight text-white sm:text-lg">
                NeuroMent
              </span>
              <span className="hidden text-xs text-teal-300/90 sm:block">
                ERP educacional · personalização por perfil
              </span>
            </div>
          </button>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-5 text-sm text-slate-300 lg:flex xl:gap-6">
            {NAV_LINKS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => scrollToId(id)}
                className="relative transition-colors hover:text-white after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-0 after:bg-teal-400/70 after:transition-all after:duration-300 hover:after:w-full"
              >
                {label}
              </button>
            ))}
          </nav>

          {/* CTA buttons */}
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogin}
              className="h-9 rounded-full border border-white/12 bg-white/[0.06] px-4 text-xs font-medium tracking-wide text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-md transition-all duration-300 hover:border-teal-400/40 hover:bg-white/[0.11] hover:text-white hover:shadow-[0_0_28px_-8px_rgba(45,212,191,0.5)] sm:px-5 sm:text-sm"
            >
              <LogIn className="mr-1.5 h-3.5 w-3.5 shrink-0 opacity-90 sm:h-4 sm:w-4" aria-hidden />
              Entrar
            </Button>
            <Button
              size="sm"
              onClick={() => goInstitutionWithPlan("educacao")}
              className="landing-cta-glow hidden bg-gradient-to-r from-teal-500 to-cyan-600 px-3 text-xs font-semibold shadow-lg shadow-teal-900/40 sm:inline-flex sm:px-4 sm:text-sm"
            >
              Solicitar demo
            </Button>

            {/* Mobile menu */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/10 lg:hidden"
                  aria-label="Abrir menu"
                >
                  <Menu className="h-5 w-5" aria-hidden />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[min(100vw-1rem,20rem)] border-white/10 bg-[#050a14] text-white"
              >
                <SheetHeader>
                  <SheetTitle className="text-left text-white">Menu</SheetTitle>
                </SheetHeader>
                <nav className="mt-6 flex flex-col gap-1">
                  {NAV_LINKS.map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => scrollToSection(id)}
                      className="rounded-lg px-3 py-3 text-left text-sm text-slate-200 hover:bg-white/10"
                    >
                      {label}
                    </button>
                  ))}
                </nav>
                <div className="mt-6 flex flex-col gap-2 border-t border-white/10 pt-6">
                  <Button
                    variant="ghost"
                    className="h-11 rounded-full border border-white/15 bg-white/[0.06] text-sm font-medium tracking-wide text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-md transition-all hover:border-teal-400/35 hover:bg-white/[0.1] hover:shadow-[0_0_24px_-8px_rgba(45,212,191,0.45)]"
                    onClick={handleLogin}
                  >
                    <LogIn className="mr-2 h-4 w-4 opacity-90" aria-hidden />
                    Entrar
                  </Button>
                  <Button
                    className="bg-gradient-to-r from-teal-500 to-cyan-600 font-semibold"
                    onClick={() => goInstitutionWithPlan("educacao")}
                  >
                    Solicitar demonstração
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-slate-400"
                    onClick={() => navigate("/signup")}
                  >
                    Cadastro usuário (piloto)
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* ── Page content ───────────────────────────────────────────── */}
      <main id="top" className="pt-16 sm:pt-[68px]">
        <HeroSection
          heroSectionRef={heroSectionRef}
          heroScrollProgress={heroScrollProgress}
          onLogin={handleLogin}
          onDemoCta={() => goInstitutionWithPlan("educacao")}
        />

        <ProblemSection />

        <JourneySection />

        <section id="modulos">
          <ModulesSection />
        </section>

        <AiSection onDemoCta={() => goInstitutionWithPlan("educacao")} />

        <PersonasSection />

        <TrustSection />

        <TestimonialsSection />

        <PlansSection
          onPlanSelect={goInstitutionWithPlan}
          onSignup={() => navigate("/signup")}
        />

        <FaqSection />

        <FinalCta
          onDemoCta={() => goInstitutionWithPlan("educacao")}
          onLogin={handleLogin}
        />
      </main>

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <footer className="border-t border-white/10 bg-[#030711] py-10 pb-[max(2.5rem,calc(env(safe-area-inset-bottom,0px)+1.5rem))] sm:py-12">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-8 sm:grid-cols-2 sm:gap-10 lg:grid-cols-4">
            <div>
              <div className="mb-4 flex items-center gap-2">
                <div className="rounded-lg bg-gradient-to-br from-teal-400 to-cyan-500 p-2">
                  <Brain className="h-5 w-5 text-white" aria-hidden />
                </div>
                <span className="font-display text-lg font-semibold text-white">
                  NeuroMent
                </span>
              </div>
              <p className="text-sm leading-relaxed text-slate-400">
                Personalização por perfil e comportamento, com camadas separadas para
                estudo e para avaliação formal. Módulo institucional em evolução; núcleo
                do estudante disponível para validar a hipótese do produto.
              </p>
            </div>
            <div>
              <h4 className="mb-4 text-sm font-semibold text-white">Produto</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {NAV_LINKS.map(({ id, label }) => (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => scrollToId(id)}
                      className="hover:text-white"
                    >
                      {label}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    onClick={() => navigate("/cadastro-instituicao")}
                    className="hover:text-white"
                  >
                    Cadastro de instituição
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 text-sm font-semibold text-white">Institucional</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><span className="cursor-default">Implementação e treinamento</span></li>
                <li><span className="cursor-default">API e integrações</span></li>
                <li><span className="cursor-default">Suporte dedicado</span></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 text-sm font-semibold text-white">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><button type="button" className="hover:text-white">Termos de uso</button></li>
                <li><button type="button" className="hover:text-white">Privacidade</button></li>
                <li><button type="button" className="hover:text-white">Contato</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-8 text-center text-xs text-slate-500 sm:mt-10 sm:flex-row sm:text-left sm:text-sm">
            <p>© 2026 NeuroMent. Todos os direitos reservados.</p>
            <div className="flex max-w-md items-start justify-center gap-2 sm:max-w-none sm:justify-end">
              <Shield className="mt-0.5 h-4 w-4 shrink-0 text-teal-500/80" aria-hidden />
              <span className="text-left leading-snug">
                Dados tratados com critérios de segurança e privacidade
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
